import { SxProps, Theme, Typography, useTheme } from "@mui/material";
import Autocomplete, { AutocompleteChangeReason, AutocompleteRenderGroupParams } from "@mui/material/Autocomplete";
import TextField, { OutlinedTextFieldProps } from "@mui/material/TextField";
import PLPill from "components/patternLib/PLPill";
import React from "react";
import {
  inputLabelStyles,
  autocompleteRootStyles,
  inputStyles,
  InputWrapperSC,
  InputLabelWrapperSC,
  InputChipWrapperSC,
  GroupHeaderSC,
  GroupItemsSC,
  MultiSelectOptionListItemSC,
} from "./styles";
import translations from "translations";

export interface SelectItem {
  label: string;
  value: string;
}

interface MultiSelectFreeSoloProps {
  createOptionLabel?: (inputValue: string) => string;
  customSx?: SxProps<Theme>;
  disabled?: boolean;
  enableCreateOption?: boolean;
  existingGroupLabel?: string;
  filterOptions?: (options: SelectItem[], state: object) => SelectItem[];
  inputValue?: string;
  isStatusError?: boolean;
  label?: string;
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Array<string | SelectItem>,
    reason: AutocompleteChangeReason
  ) => void;
  onChangeInput?: OutlinedTextFieldProps["onChange"];
  options: SelectItem[];
  placeholder?: string;
  selected?: SelectItem[];
}

const defaultCreateLabel = (v: string): string => `${translations.globals.terms.create} '` + v + `'`;

const MultiSelectFreeSolo: React.FC<MultiSelectFreeSoloProps> = ({
  options,
  onChange,
  placeholder,
  disabled,
  selected = [],
  label,
  onChangeInput,
  inputValue,
  filterOptions,
  customSx,
  isStatusError,
  enableCreateOption = true,
  createOptionLabel = defaultCreateLabel,
  existingGroupLabel = translations.globals.terms.existingTags,
}) => {
  const theme = useTheme();
  const [internalInput, setInternalInput] = React.useState<string>("");
  const hasInput: boolean = internalInput.trim().length > 0;

  const isSelected = (opt: SelectItem): boolean =>
    selected.some((sel) =>
      sel.value && opt.value ? sel.value === opt.value : sel.label.toLowerCase() === opt.label.toLowerCase()
    );

  const groupedFilterOptions = (opts: SelectItem[], state: { inputValue: string }): SelectItem[] => {
    const trimmed: string = state.inputValue.trim();
    const lowerInput: string = trimmed.toLowerCase();
    const available: SelectItem[] = opts.filter((opt) => !isSelected(opt));
    const filtered: SelectItem[] =
      trimmed.length === 0 ? available : available.filter((opt) => opt.label.toLowerCase().includes(lowerInput));
    const isExisting: boolean = trimmed.length > 0 && opts.some((opt) => opt.label.toLowerCase() === lowerInput);
    if (trimmed.length > 0 && !isExisting) {
      const createOpt: SelectItem = { label: trimmed, value: "" };
      return [createOpt, ...filtered];
    }
    return filtered;
  };

  const wrappedFilterOptions = filterOptions
    ? (opts: SelectItem[], state: object): SelectItem[] => filterOptions(opts, state).filter((opt) => !isSelected(opt))
    : undefined;

  const optionsHasInputLabel = options?.find((opt) => opt.label.toLowerCase() === internalInput.toLowerCase());

  return (
    <InputWrapperSC sx={customSx}>
      {label && (
        <InputLabelWrapperSC>
          <Typography style={inputLabelStyles} variant="copy">
            {label}
          </Typography>
        </InputLabelWrapperSC>
      )}
      <Autocomplete
        sx={{ ...autocompleteRootStyles(isStatusError), ...customSx }}
        multiple
        id="tags-filled"
        options={options}
        value={selected}
        placeholder={placeholder}
        autoHighlight
        filterOptions={enableCreateOption ? groupedFilterOptions : wrappedFilterOptions}
        groupBy={
          enableCreateOption && !optionsHasInputLabel
            ? (option: SelectItem): string => (option.value === "" || !hasInput ? "" : existingGroupLabel)
            : undefined
        }
        freeSolo
        onInputChange={(_e, newValue) => setInternalInput(newValue)}
        getOptionLabel={(option: SelectItem | string) => {
          if (typeof option === "string") {
            return option;
          } else {
            return option.label;
          }
        }}
        disabled={disabled}
        onChange={onChange}
        renderInput={(params) => (
          <TextField {...params} onChange={onChangeInput} value={inputValue} sx={inputStyles} variant="outlined" />
        )}
        renderOption={
          enableCreateOption
            ? (props, option: SelectItem | string) => {
                const isCreate = typeof option !== "string" && option.value === "";
                const text = typeof option === "string" ? option : option.label;
                return (
                  <MultiSelectOptionListItemSC
                    {...props}
                    sx={{ color: isCreate ? theme.colors.grey600 : "inherit" }}
                    key={isCreate ? "__create__" + text : text}
                  >
                    {isCreate ? createOptionLabel(text) : text}
                  </MultiSelectOptionListItemSC>
                );
              }
            : undefined
        }
        renderGroup={
          enableCreateOption && !optionsHasInputLabel
            ? (params: AutocompleteRenderGroupParams) => (
                <li key={params.key}>
                  {params.group ? <GroupHeaderSC>{params.group}</GroupHeaderSC> : null}
                  <GroupItemsSC>{params.children}</GroupItemsSC>
                </li>
              )
            : undefined
        }
        renderTags={() => null}
        // renderTags={(value: Array<SelectItem | string>, getTagProps) => {
        //   return selected.map((optionIn: SelectItem | string, index: number) => {
        //     const option = typeof optionIn === "string" ? optionIn : optionIn?.label;
        //     return (
        //       <InputChipWrapperSC key={option}>
        //         <PLPill
        //           disabled={disabled}
        //           label={option}
        //           lhClose={getTagProps({ index }).onDelete}
        //           deleteIcon
        //           height="25px"
        //         />
        //       </InputChipWrapperSC>
        //     );
        //   });
        // }}
      />
    </InputWrapperSC>
  );
};

export default MultiSelectFreeSolo;
